<template>
    <div class="card">
      <div class="card-header flex-wrap border-0 pt-6 pb-0">
        <div class="card-title">
          <div class="card-label">
            <h3 class="card-label">{{ $t('absences.title') }}</h3>
            <p class="text-muted">{{ $t('absences.list') }}</p>
          </div>
        </div>
        <div class="card-toolbar">
          <ButtonDropdownFilter
            :search="true"
            :options="Object.values(absenceTypes)"
            value-field="id"
            :name-field="name"
            v-model="filterList.type"
          />
          <ButtonDropdownFilter
            :search="false"
            :options="Object.values(absencesStatus)"
            value-field="value"
            :name-field="locale"
            v-model="filterList.status"
          />
        </div>
      </div>
      <div class="card-body">
        <div class="row">
          <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4"
                id="absences_datatable">
            <thead>
              <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
                <th class="min-w-100px text-center">{{ $t('absences.create_date') }}</th>
                <th class="min-w-100px text-center">{{ $t('absences.user') }}</th>
                <th class="min-w-100px text-center">{{ $t('absences.start_date') }}</th>
                <th class="min-w-100px text-center">{{ $t('absences.end_date') }}</th>
                <th class="min-w-100px text-center">{{ $t('absences.type') }} </th>
                <th class="min-w-100px text-center">{{ $t('absences.number') }}</th>
                <th class="min-w-100px text-center">{{ $t('absences.status') }} </th>
                <th class="min-w-100px text-center">{{ $t('absences.actions') }}</th>
              </tr>
            </thead>
          </table>
        </div>
      </div>

      <DetailModal id="detail_absence_modal" :form="absence"/>
      <RejectModal id="reject_absence_modal" @load="loadData" :form="formReject"/>
      <FileModal id="files_modal" :files="absence.files"/>
    </div>
</template>

<script setup>
import {computed, nextTick, onMounted, ref, watch, defineEmits, defineProps} from "vue";
import SubLayout from "@/views/Layouts/partials/SubLayout.vue";
import {useStore} from "vuex";
import i18n from "@/plugins/i18n";
import Ls from "@/services/ls";
import {BASE_URL} from "@/services/api";
import {formattedLocalAmount} from "@/mixins/Helpers";
import {useRouter, useRoute} from "vue-router";
import {useLocales} from "@/composables/locales";
import {useHasAbility} from "@/composables/hasAbility";
import Card from "@/Modules/CRM/components/Card";
import Button from "@/components/Button";
import abilities from "@/composables/abilities";
import {Theme} from "@/composables/theming";
import ButtonDropdownFilter from "@/components/ButtonDropdownFilter.vue";
import OutlineButton from "@/components/OutlineButton.vue"
import DetailModal from "./Modals/DetailsModal"
import RejectModal from "./Modals/rejectModal"
import {useSwal} from "@/composables/useSwal";
import { useI18n } from 'vue-i18n'
import FileModal from "./Modals/FilesAttachModal"

const route = useRoute()
const router = useRouter()

const store = useStore()
const { can } = useHasAbility()
const {showSwal} = useSwal()
const { t } = useI18n({ useScope: 'global' })

const {locale, datatableLocaleUrl} = useLocales()

const emits = defineEmits(['loadStat'])

const props = defineProps({
    filterDate: {
      type: Object,
      required: true
    },
    officeId: {
      type: String,
      required: true
    }
})

const table = ref()
const filterList = ref({
  status: 'all',
  type: ''
})

const absence = ref({})
const formReject = ref({
  id: '',
  reason: ''
})

const absencesStatus = computed(() => store.getters['absences/getAbsencesStatus'])
const absenceTypes = computed(() => {
  return [{ id: '', name: t('absences.all_types') }, ...store.getters['absence_types/getAbsenceTypes']];
});
watch(() => locale.value, async(value) => {
  if (table.value) {
    await nextTick();
    loadData();
  }
}, { deep: true })

watch(() => filterList.value.status, (newValue) => {
  table.value?.ajax.url(`${BASE_URL}/get/holidays/datatables?status=${filterList.value.status}&holiday_type_id=${filterList.value.type}&office_id=${props.officeId}&date_start=${props.filterDate.start_date}&date_end=${props.filterDate.end_date}`).load()
})

watch(() => filterList.value.type, (newValue) => {
  table.value?.ajax.url(`${BASE_URL}/get/holidays/datatables?status=${filterList.value.status}&holiday_type_id=${filterList.value.type}&office_id=${props.officeId}&date_start=${props.filterDate.start_date}&date_end=${props.filterDate.end_date}`).load()
})

watch(() => props.officeId, (newValue) => {
  table.value?.ajax.url(`${BASE_URL}/get/holidays/datatables?status=${filterList.value.status}&holiday_type_id=${filterList.value.type}&office_id=${props.officeId}&date_start=${props.filterDate.start_date}&date_end=${props.filterDate.end_date}`).load()
})

watch(() => props.filterDate.start_date, (newValue) => {
  table.value?.ajax.url(`${BASE_URL}/get/holidays/datatables?status=${filterList.value.status}&holiday_type_id=${filterList.value.type}&office_id=${props.officeId}&date_start=${props.filterDate.start_date}&date_end=${props.filterDate.end_date}`).load()
})

watch(() => props.filterDate, (newValue) => {
  table.value?.ajax.url(`${BASE_URL}/get/holidays/datatables?status=${filterList.value.status}&holiday_type_id=${filterList.value.type}&office_id=${props.officeId}&date_start=${props.filterDate.start_date}&date_end=${props.filterDate.end_date}`).load()
})

function getAbsenceStatusName(status) {
  const {color, fr, en} = absencesStatus.value?.find(el => el.value === status) || ''
  return {color, fr, en}
}

function fetchDatas() {
  const AUTH_TOKEN = Ls.get('auth.token')
  const pageLimit = Ls.get('pageLength')

  table.value = $("#absences_datatable").DataTable({
    destroy: true,
    responsive: !0,
    searchDelay: 500,
    processing: !0,
    serverSide: !0,
    pageLength: pageLimit ||  5,
    lengthMenu: [5, 10, 25, 50, 75, 100 ],
    order: [[ 0, 'desc']],
    ajax: {
      url: BASE_URL + `/get/holidays/datatables?status=${filterList.value.status}&holiday_type_id=${filterList.value.type}&office_id=${props.officeId}&date_start=${props.filterDate.start_date}&date_end=${props.filterDate.end_date}`,
      type: "GET",
      beforeSend: function (request) {
        request.setRequestHeader("Authorization", "Bearer " + AUTH_TOKEN);
        request.setRequestHeader("Company", Ls.get('current.company'));
      }
    },
    columns: [
    { data: "created_at" }, { data: "user_name" }, { data: "start_date" }, { data: "end_date" }, { data: "holiday_type_name" }, { data: "total_number_days" }, { data: "status" }, { data: "Actions", responsivePriority: -1 }
    ],
    columnDefs: [
      {
        targets: -8,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -7,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -6,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -5,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -4,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -3,
        order: "desc",
        className: "dt-center text-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },
      {
        targets: -2,
        orderable: false,
        className: "dt-center text-center" ,
        render: function (t, a, e, l) {
          let status = getAbsenceStatusName(t)
          const locale = i18n.global.locale.value
          let color = status.color
          status = status[locale]
          return '<span class="badge badge-light-' + color +'" style="font-size:13px; margin:3px; padding:5px"><span style="margin-left: 5px" class="valueRow' + l.row + ' label label-success label-inline font-weight-bolder mr-2 edit_value_btn">' + status + '</span></span>'
        },
      },
      {
        width: "10%",
        targets: -1,
        "className": "dt-center text-start",
        title: "Actions",
        orderable: !1,
        render: (t, a, e, l) => {
          let render=""
          render+='<a href="javascript:;" data-id="'+ e.id +'"  data-type="'+ e.id +'" class="btn btn-sm btn-clean btn-icon show_btn" title="'+ i18n.global.t('button.show') +'"><i  data-id="'+ e.id +'" class="fas fa-eye fs-2" style="color: '+Theme.gray +'"></i></a>'
          if(e.status == 'UNVALIDATED'){
            render+='<a href="javascript:;" data-id="'+ e.id +'"  class="btn btn-sm btn-clean btn-icon approbate_btn" title="'+ i18n.global.t('button.approbate') +'"><i  data-id="'+ e.id +'" class="fa fa-thumbs-up fs-2"  style="color: '+Theme.gray +'"></i></a>'
          }
          if(e.status == 'UNVALIDATED' || e.status == 'APPROVED'){
            render+='<a href="javascript:;" data-id="'+ e.id +'"  class="btn btn-sm btn-clean btn-icon reject_btn" title="'+ i18n.global.t('button.reject') +'"><i  data-id="'+ e.id +'" class="fas fa-window-close fs-2"  style="color: '+Theme.gray +'"></i></a>'
          }
          if(e.status == 'APPROVED'){
            render+='<a href="javascript:;" data-id="'+ e.id +'"  class="btn btn-sm btn-clean btn-icon validate_btn" title="'+ i18n.global.t('button.validate') +'"><i  data-id="'+ e.id +'" class="fas fa-check-square fs-2"  style="color: '+Theme.gray +'"></i></a>'
          }
          if(e.hasFile){
            render+='<a href="javascript:;" data-id="'+ e.id +'"  data-type="'+ e.id +'" class="btn btn-sm btn-clean btn-icon files_btn" title="'+ i18n.global.t('absences.add_file') +'"><i  data-id="'+ e.id +'" class="fa fa-file fs-2" style="color: '+Theme.gray +'"></i></a>'
          }
          return render
        },
      },

    ],
    language: {
      url: datatableLocaleUrl.value,
    }
  });

  $(document).off("click", "#absences_datatable .show_btn").on("click", "#absences_datatable .show_btn", (e) => {
    let id = e.target.getAttribute('data-id')
    openDetailModal(id, false)
  });

  $(document).off("click", "#absences_datatable .files_btn").on("click", "#absences_datatable .files_btn", (e) => {
    let id = e.target.getAttribute('data-id')
    openDetailModal(id, true)
  });

  $(document).off("click", "#absences_datatable .approbate_btn").on("click", "#absences_datatable .approbate_btn", async(e) => {
    let id = e.target.getAttribute('data-id')
    await approbateAbsence(id)
  });

  $(document).off("click", "#absences_datatable .validate_btn").on("click", "#absences_datatable .validate_btn", async(e) => {
    let id = e.target.getAttribute('data-id')
    await validateAbsence(id)
  });

  $(document).off("click", "#absences_datatable .reject_btn").on("click", "#absences_datatable .reject_btn", async(e) => {
    let id = e.target.getAttribute('data-id')
    formReject.value.id = id
    $('#reject_absence_modal').modal('toggle')
  });
}

function loadData() {
  fetchDatas()
  emits('loadStat')
}

function openDetailModal(id, getFile){
  store.dispatch('absences/showAbsence', id)
      .then((res) => {
          let data = res.data?.holiday
          if (!data) return;

          absence.value.user = data?.user?.fullname
          absence.value.type = data?.holiday_type?.name
          absence.value.start_date = data?.start_date
          absence.value.end_date = data?.end_date

          let status = getAbsenceStatusName(data?.status)
          const lang = i18n.global.locale.value

          absence.value.status = status[lang]
          absence.value.color = status['color']
          absence.value.description = data?.description
          absence.value.motif = data?.reason
          absence.value.files = data?.files

          absence.value.validator = data.validator?.fullname
          absence.value.approved_owner = data.approved_owner?.fullname
          absence.value.rejected_owner = data.rejected_owner?.fullname 

          if(getFile){
            $('#files_modal').modal('toggle')
          }else{
            $('#detail_absence_modal').modal('toggle')
          }
      })
}

async function approbateAbsence(id){
  showSwal(
      '',
      t('absences.swal.approbate_text'),
      'warning',
      '#F07817'
  ).then( async (result) => {
    if (result.value) {
      try {
        store.dispatch('absences/approbateAbsence', id)
          .then(() => {
              loadData()
              window.toastr['success'](i18n.global.t('absences.absence_approbated_successfully'))
          })
      } catch (e) {
      }
    }
  });
}

async function validateAbsence(id){
  showSwal(
      '',
      t('absences.swal.validate_text'),
      'warning',
      '#F07817'
  ).then( async (result) => {
    if (result.value) {
      try {
        store.dispatch('absences/validateAbsence', id)
          .then(() => {
              loadData()
              window.toastr['success'](i18n.global.t('absences.absence_validated_successfully'))
          })
      } catch (e) {
      }
    }
  });
}

onMounted(async() => {
  await store.dispatch('absences/loadAbsencesStatus')
  await store.dispatch('absence_types/loadAbsenceTypes') 
  fetchDatas()
})

</script>
